import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import CombinationsProductTable from '@/modules/ConfiguratorCombinations/components/CombinationsProductTable.vue';
import CombinationsProductsControls from '@/modules/ConfiguratorCombinations/components/CombinationsProductsControls.vue';
import CombinationsSelectorProgressBar from '@/modules/ConfiguratorCombinations/components/CombinationsSelectorProgressBar.vue';
import CombinationsStepInfo from '@/modules/ConfiguratorCombinations/components/CombinationsStepInfo.vue';
import ShapeAndAngle from '@/modules/ConfiguratorJoints/components/ShapeAndAngle.vue';
import GuidesAndType from '@/modules/ConfiguratorJoints/components/GuidesAndType.vue';
import PreferenceModal from '@/modules/ConfiguratorJoints/components/PreferenceModal.vue';
import AdditionalOptions from '@/modules/ConfiguratorJoints/components/AdditionalOptions.vue';
import OptionsPreview from '@/modules/ConfiguratorJoints/components/OptionsPreview.vue';
import JointsConfiguratorTemplate from '@/modules/ConfiguratorJoints/components/JointsConfiguratorTemplate.vue';
import JointsParameterList from '@/modules/ConfiguratorJoints/components/JointsParameterList.vue';
import JointsOptionsIcons from '@/modules/ConfiguratorJoints/components/JointsOptionsIcons.vue';
import { useJointsTableData } from '@/modules/ConfiguratorJoints/composables/useJointsTableData';
import { useJointsStepNavigation } from '@/modules/ConfiguratorJoints/composables/useJointsStepNavigation';
import { useShapeAndAngle } from '@/modules/ConfiguratorJoints/composables/useShapeAndAngle';
import { CornerType, GuideType, useGuidesAndType } from '@/modules/ConfiguratorJoints/composables/useGuidesAndType';
import { useOptions } from '@/modules/ConfiguratorJoints/composables/useOptions';
import { downloadStageAsPng, getStageAsPngBase64 } from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';

import {
    jointsConfiguration,
    MAX_SELECTED_POSITIONS_FOR_JOINT,
    additionalDescriptions,
    defaultCanvasTemplateConfigurations,
    getJointsParameterRows,
} from '@/modules/ConfiguratorJoints/helpers/JointsConfiguration';
import { GlobalOptions } from '@/enums/global/GlobalOptions';
import DownloadButton from '@/shared/components/DownloadButton.vue';
import store from '@/store';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import { JOINTS_OPTIONS } from '@/enums/global/GlobalOptions';

interface OpenJointsConfiguratorPayload {
    rows: ProductRowItem[];
    productName: string;
}

// ============================================================================
// Popup lifecycle state (kept inline — small and presentational)
// ============================================================================

export default /*#__PURE__*/_defineComponent({
  __name: 'JointsConfiguratorSelectorPopup',
  setup(__props) {

const visible = ref<boolean>(false);
const productData = ref<ProductRowItem[]>([]);
const productName = ref<string>('');

// ============================================================================
// General configuration + constants (kept inline — small and presentational)
// And small helper functions
// ============================================================================

const jointsTemplate = ref<any>(null);
const jointsOptionsIcons = ref<any>(null);
const longerArm = ref<'1' | '2' | ''>('');
const preferencePopupVisible = ref<boolean>(false);

const jointsParameterRows = computed(() =>
    getJointsParameterRows({
        selectedShape: selectedShape.value,
        angle: angle.value,
        guideTypes: individualGuideTypes.value,
        cornerType: cornerType.value as any,
        longerArm: longerArm.value as any,
    }),
);

const disableDownloadButton = computed(() => {
    // Are we at the last canvas step
    if (currentStep.value !== totalSteps.value) {
        return true;
    }

    // Are there enough products selected for joint
    if (productsSelectedForJoint.value.length < 2) {
        return true;
    }

    // Check shape
    if (!selectedShape.value) {
        return true;
    }

    return false;
});

async function onDownloadCanvas() {
    const stageInstance =
        jointsTemplate.value?.$refs?.jointsCanvas?.getNode?.() ??
        jointsTemplate.value?.$refs?.jointsCanvas?.getStage?.();

    if (!stageInstance) {
        return;
    }

    await downloadStageAsPng(
        stageInstance,
        `joint_${productName.value}`,
        jointsOptionsIcons.value?.$el,
        jointsParameterRows.value,
        selectedOptions.value,
    );
}

// ============================================================================
// Per-product configuration (checklist + canvas templates, etc.)
// Mirrors the combinations `currentConfig` pattern.
// ============================================================================
const currentConfig = computed(() => jointsConfiguration.find((entry) => entry.products.includes(productName.value)));

const canvasTemplateConfigurations = computed(
    () => currentConfig.value?.canvasTemplateConfigurations ?? defaultCanvasTemplateConfigurations,
);

const maxSelectedPositions = computed(
    () => currentConfig.value?.maxSelectedPositions ?? MAX_SELECTED_POSITIONS_FOR_JOINT,
);

// ============================================================================
// Table data + selection + form-field handlers
// ============================================================================
const {
    tableHeaderFields,
    productRowsFormData,
    productsSelectedForJoint,
    isPositionSelectionCompleted,
    isPositionOrderCompleted,
    toggleProductSelection,
    onFormFieldChange,
    clearAssemblyPositionValues,
    renderFormData,
    resetTableData,
} = useJointsTableData(productData, maxSelectedPositions, currentConfig);

// Dampens how strongly width differences stretch the arm visuals.
// 1   → raw ratio (50% wider product ⇒ 50% longer arm)
// 0.4 → 50% wider product ⇒ 20% longer arm  (current setting)
// 0   → all arms rendered equal length regardless of width
const SIZE_FACTOR_DAMPENING = 0.4;

function buildProductSizeFactors(productIds: number[]): Record<number, { productSizeFactor: number }> {
    const widths: Record<number, number> = {};

    for (const productFormId of productIds) {
        const row = productRowsFormData.value.find((r) => r.activeProductFormId === productFormId);
        const widthField = row?.formData.find((f) => f.fieldPID === GlobalOptions.WIDTH_PID);
        const value = Number(widthField?.fieldValue);
        if (value > 0) {
            widths[productFormId] = value;
        }
    }

    const allWidths = Object.values(widths);
    if (allWidths.length === 0) {
        return {};
    }

    const minWidth = Math.min(...allWidths);
    const sizeFactors: Record<number, { productSizeFactor: number }> = {};

    for (const [productId, width] of Object.entries(widths)) {
        const rawFactor = width / minWidth;
        const dampenedFactor = 1 + (rawFactor - 1) * SIZE_FACTOR_DAMPENING;

        sizeFactors[Number(productId)] = {
            productSizeFactor: dampenedFactor,
        };
    }

    return sizeFactors;
}

const productsSizeFactors = computed<Record<number, { productSizeFactor: number }>>(() =>
    buildProductSizeFactors(productRowsFormData.value.map((row) => row.activeProductFormId)),
);

function getProductAssemblyPosition(productFormId: number): 1 | 2 | null {
    const row = productRowsFormData.value.find((r) => r.activeProductFormId === productFormId);
    const posField = row?.formData.find((f) => f.fieldPID === String(JOINTS_OPTIONS.JOINTS_POSITION_PID));
    const position = Number(posField?.fieldValue);

    if (position === 1 || position === 2) {
        return position;
    }

    return null;
}

const productAssemblyPositions = computed<Record<number, 1 | 2>>(() =>
    Object.fromEntries(
        productsSelectedForJoint.value.flatMap((productFormId) => {
            const position = getProductAssemblyPosition(productFormId);
            return position === null ? [] : [[productFormId, position]];
        }),
    ),
);

const canvasProductAssemblyPositions = computed<Record<number, 1 | 2>>(() => {
    const positions = { ...productAssemblyPositions.value };
    const assignedPositions = new Set(Object.values(positions));
    const availablePositions = ([1, 2] as const).filter((position) => !assignedPositions.has(position));
    const productsWithoutPosition = productsSelectedForJoint.value.filter((productFormId) => !positions[productFormId]);

    productsWithoutPosition.forEach((productFormId, index) => {
        const position = availablePositions[index];
        if (position) {
            positions[productFormId] = position;
        }
    });

    return positions;
});

// Ordered by Assembly Position field value (1 = left arm, 2 = right arm).
// Falls back to selection order for products without a position assigned yet
// so the canvas still renders something during step 2.
const productsByArmPosition = computed<number[]>(() => {
    function getPosition(productFormId: number): number {
        const pos = getProductAssemblyPosition(productFormId);
        return pos ?? Number.MAX_SAFE_INTEGER;
    }
    return [...productsSelectedForJoint.value].sort((a, b) => getPosition(a) - getPosition(b));
});

const canvasVisibleArm = computed(() => {
    if (currentStep.value !== 1) {
        return 'both';
    }

    if (productsSelectedForJoint.value.length === 0) {
        return 'none';
    }

    return productsSelectedForJoint.value.length === 1 ? 'left' : 'both';
});

const canvasProductsByArmPosition = computed(() => {
    if (currentStep.value === 1) {
        return productsSelectedForJoint.value;
    }

    return [...productsSelectedForJoint.value].sort((a, b) => {
        const positionA = canvasProductAssemblyPositions.value[a] ?? Number.MAX_SAFE_INTEGER;
        const positionB = canvasProductAssemblyPositions.value[b] ?? Number.MAX_SAFE_INTEGER;
        return positionA - positionB;
    });
});

const canvasAssemblyPositionVisibility = computed(() => {
    if (currentStep.value < 2) {
        return { left: false, right: false };
    }

    let left = false;
    let right = false;

    productsSelectedForJoint.value.forEach((productFormId) => {
        const position = getProductAssemblyPosition(productFormId);

        if (position === 1) {
            left = true;
        }

        if (position === 2) {
            right = true;
        }
    });

    return { left, right };
});

// ============================================================================
// Shape + angle (step 3)
// ============================================================================
const {
    selectedShape,
    angle,
    angleInputRenderKey,
    isShapeAndAngleCompleted,
    setSelectedShape,
    setAngle,
    resetShapeAndAngle,
} = useShapeAndAngle();

// ============================================================================
// Guides + type (step 4)
// ============================================================================
const {
    guideType,
    individualGuidesEnabled,
    individualGuideTypes,
    cornerType,
    areGuidesConfigured,
    setAllGuideTypes,
    setIndividualGuideType,
    setIndividualGuidesEnabled,
    resetGuides,
    setCornerType,
    resetGuidesAndType,
    areAllGuidesOfType,
    guidesAndTypeConfig,
    getGuideTypeChecklistValue,
} = useGuidesAndType(currentConfig);

function openPreferencePopup(value: CornerType) {
    if (value === 'type-2') {
        setCornerType(value);
        return;
    }

    // longerArm.value = '';
    preferencePopupVisible.value = true;
}

function closePreferencePopup() {
    preferencePopupVisible.value = false;
}

function selectLongerArm(position: '1' | '2') {
    longerArm.value = position;
    setCornerType('type-1');

    const guideType = individualGuideTypes.value[1].leftCorner;

    if (guideType === 'alu-guide' || guideType === 'no-guide') {
        setAllGuideTypes(null);
    }

    closePreferencePopup();
}

// ============================================================================
// Additional options (step 5 — conditional)
// ============================================================================
// Type 1 corners and "brez vodil" (no guide) joints have no additional
// options, so the whole step is removed from the wizard (progress bar
// included) for those configurations.
const includesOptionsStep = computed<boolean>(
    () => cornerType.value !== 'type-1' && individualGuideTypes.value[1].leftCorner !== 'no-guide',
);

const {
    options: jointOptions,
    optionTabs: jointOptionTabs,
    toggleOption,
    resetOptions,
    selectedOptions,
} = useOptions();

// ============================================================================
// Adding combination
// ============================================================================
const updateActiveProductFormValue = (payload: UpdateProductFormEntry) => {
    store.dispatch('configurator/updateActiveProductFormValue', payload);
};

async function captureJointImageBase64(): Promise<string | null> {
    const stageInstance =
        jointsTemplate.value?.$refs?.jointsCanvas?.getNode?.() ??
        jointsTemplate.value?.$refs?.jointsCanvas?.getStage?.();
    if (!stageInstance) {
        return null;
    }
    return await getStageAsPngBase64(
        stageInstance,
        jointsOptionsIcons.value?.$el,
        jointsParameterRows.value,
        selectedOptions.value,
    );
}

async function addCombination() {
    formatDataForMultipositionUpdate();
    updateMultiposition();
    const combinationImageBase64 = await captureJointImageBase64();
    const selectedRows = productData.value.filter((row) =>
        productsSelectedForJoint.value.includes(row.activeProductFormId),
    );
    nextTick(() => {
        EventBus.$emit(
            EventBusEvents.addNewCombinationFromConfigurator,
            selectedRows,
            true,
            combinationImageBase64,
            productName.value,
            'kotni',
        );
        closePopup();
    });
}

function formatDataForMultipositionUpdate() {
    formatActiveSwitchForMultiposition();
    formatAdditionalOptionsForMultiposition();
    formatGuides();
}

function updateMultiposition() {
    productsSelectedForJoint.value.forEach(async (rowId: number) => {
        const productRow = productRowsFormData.value.find((row) => row.activeProductFormId === rowId);

        if (!productRow) {
            console.warn(`Product with ID ${rowId} not found in form data.`);
            return;
        }

        updateCurrentProduct(productRow);
    });
}

function updateCurrentProduct(productRow: any) {
    productRow.formData.forEach((field: any) => {
        if (field.segment === 'guides') {
        }

        if (!field.shouldUpdateConfigurator) return;

        const payload: UpdateProductFormEntry = {
            productFormId: productRow.activeProductFormId,
            pId: field.fieldPID,
            value: field.fieldValue,
        };

        console.log('Updating product form value:', payload);
        updateActiveProductFormValue(payload);
    });
}

function formatAdditionalOptionsForMultiposition() {
    const optionFields = currentConfig.value?.checklistFields.filter(
        (field: any) => field.segment === 'options' && field.shouldUpdateConfigurator,
    );

    if (!optionFields?.length) {
        return;
    }

    productsSelectedForJoint.value.forEach((rowId: number) => {
        const productRow = productRowsFormData.value.find((row) => row.activeProductFormId === rowId);

        if (!productRow) {
            return;
        }

        const formFields = optionFields.reduce((updates: Record<string, { value: any; key: string }>, field: any) => {
            const fieldKey = String(field.key);
            const option = jointOptions.value.find((entry) => entry.key === fieldKey);

            updates[fieldKey] = {
                value: option?.isSelected ?? false,
                key: 'fieldValue',
            };

            return updates;
        }, {});

        updateFormDataAttributes({
            productRow,
            formFields,
        });
    });
}

function formatGuides() {
    const guideFields = currentConfig.value?.checklistFields.filter(
        (field: any) => field.segment === 'guides' && field.shouldUpdateConfigurator,
    );

    if (!guideFields?.length) {
        return;
    }

    const productOne = productRowsFormData.value.find(
        (row) => row.activeProductFormId === productsByArmPosition.value[0],
    );
    const productTwo = productRowsFormData.value.find(
        (row) => row.activeProductFormId === productsByArmPosition.value[1],
    );

    const areAllGuidesAluGuide = areAllGuidesOfType('alu-guide');
    const areAllGuidesConcealedGuide = areAllGuidesOfType('concealed-guide');

    if (areAllGuidesAluGuide || areAllGuidesConcealedGuide) {
        const activeGuideType = areAllGuidesAluGuide ? 'alu-guide' : 'concealed-guide';
        const checklistValue = getGuideTypeChecklistValue(activeGuideType);

        // Make sure individual guide fields are not updated in multiposition
        [productOne, productTwo].forEach((productRow) => {
            updateFormDataAttributes({
                productRow: productRow,
                formFields: {
                    guideType: { value: checklistValue, key: 'fieldValue' },
                    leftGuide: { value: false, key: 'shouldUpdateConfigurator' },
                    rightGuide: { value: false, key: 'shouldUpdateConfigurator' },
                    noGuides: { value: false, key: 'fieldValue' },
                },
            });
        });

        return;
    }

    const productOneLeftGuideValue = individualGuideTypes.value[1].leftCorner;
    const productOneRightGuideValue = individualGuideTypes.value[1].rightCorner;
    const productTwoLeftGuideValue = individualGuideTypes.value[2].leftCorner;
    const productTwoRightGuideValue = individualGuideTypes.value[2].rightCorner;
    console.log(
        getGuideTypeChecklistValue(productOneLeftGuideValue),
        getGuideTypeChecklistValue(productOneRightGuideValue),
        getGuideTypeChecklistValue(productTwoLeftGuideValue),
        getGuideTypeChecklistValue(productTwoRightGuideValue),
    );
    updateFormDataAttributes({
        productRow: productOne,
        formFields: {
            guideType: { value: 'Različni vodili', key: 'fieldValue' },
            leftGuide: { value: getGuideTypeChecklistValue(productOneLeftGuideValue), key: 'fieldValue' },
            rightGuide: { value: getGuideTypeChecklistValue(productOneRightGuideValue), key: 'fieldValue' },
            noGuides: {
                value: !getGuideTypeChecklistValue(productOneRightGuideValue) ? true : false,
                key: 'fieldValue',
            },
        },
    });

    updateFormDataAttributes({
        productRow: productTwo,
        formFields: {
            guideType: { value: 'Različni vodili', key: 'fieldValue' },
            leftGuide: { value: getGuideTypeChecklistValue(productTwoLeftGuideValue), key: 'fieldValue' },
            rightGuide: { value: getGuideTypeChecklistValue(productTwoRightGuideValue), key: 'fieldValue' },
            noGuides: {
                value: !getGuideTypeChecklistValue(productTwoLeftGuideValue) ? true : false,
                key: 'fieldValue',
            },
        },
    });
}

function updateFormDataAttributes({
    productRow,
    formFields,
}: {
    productRow: any;
    formFields: Record<string, { value: any; key: string }>;
}) {
    Object.entries(formFields).forEach(([fieldName, update]) => {
        const formField = productRow.formData.find((f: any) => f.key === fieldName);
        if (formField) {
            formField[update.key] = update.value;
        }
    });
}

function formatActiveSwitchForMultiposition() {
    const activeSwitchField = currentConfig.value?.checklistFields.find(
        (field: any) => field.key === 'jointsSwitch' && field.shouldUpdateConfigurator,
    );

    if (!activeSwitchField) {
        return;
    }

    productsSelectedForJoint.value.forEach((rowId: number) => {
        const productRow = productRowsFormData.value.find((row) => row.activeProductFormId === rowId);

        if (!productRow) {
            return;
        }

        updateFormDataAttributes({
            productRow,
            formFields: {
                [String(activeSwitchField.key)]: { value: true, key: 'fieldValue' },
            },
        });
    });
}
// ============================================================================
// Step navigation
// ============================================================================
const {
    currentStep,
    stepLabels,
    currentStepDescription,
    stepsAccessible,
    canGoToPrevStep,
    canGoToNextStep,
    goToNextStep,
    goToPrevStep,
    goToStep,
    resetToFirstStep,
    totalSteps,
} = useJointsStepNavigation({
    isPositionSelectionCompleted,
    isPositionOrderCompleted,
    isShapeAndAngleCompleted,
    areGuidesConfigured,
    includesOptionsStep,
    clearAssemblyPositionValues,
    resetShapeAndAngle,
    resetGuidesAndType,
    resetOptions,
});

const isOnLastStep = computed<boolean>(() => currentStep.value === stepLabels.value.length);

const isOnOptionsStep = computed<boolean>(() => includesOptionsStep.value && currentStep.value === 5);

const isOnPreviewStep = computed<boolean>(() => currentStep.value === totalSteps.value);

// ============================================================================
// Popup lifecycle
// ============================================================================
async function openPopup(payload: OpenJointsConfiguratorPayload) {
    visible.value = true;
    productData.value = payload.rows;
    productName.value = payload.productName;

    await nextTick();
    renderFormData();
}

function closePopup() {
    resetTableData();
    resetShapeAndAngle();
    resetGuidesAndType();
    resetOptions();
    resetToFirstStep();
    closePreferencePopup();
    longerArm.value = '';
    productData.value = [];
    productName.value = '';
    visible.value = false;
}

onMounted(() => {
    EventBus.$on(EventBusEvents.openJointsConfiguratorSelectorPopup, openPopup);
    EventBus.$on(EventBusEvents.closeContactPopup, closePopup);
});

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.openJointsConfiguratorSelectorPopup, openPopup);
    EventBus.$off(EventBusEvents.closeContactPopup, closePopup);
});

return { __sfc: true,visible, productData, productName, jointsTemplate, jointsOptionsIcons, longerArm, preferencePopupVisible, jointsParameterRows, disableDownloadButton, onDownloadCanvas, currentConfig, canvasTemplateConfigurations, maxSelectedPositions, tableHeaderFields, productRowsFormData, productsSelectedForJoint, isPositionSelectionCompleted, isPositionOrderCompleted, toggleProductSelection, onFormFieldChange, clearAssemblyPositionValues, renderFormData, resetTableData, SIZE_FACTOR_DAMPENING, buildProductSizeFactors, productsSizeFactors, getProductAssemblyPosition, productAssemblyPositions, canvasProductAssemblyPositions, productsByArmPosition, canvasVisibleArm, canvasProductsByArmPosition, canvasAssemblyPositionVisibility, selectedShape, angle, angleInputRenderKey, isShapeAndAngleCompleted, setSelectedShape, setAngle, resetShapeAndAngle, guideType, individualGuidesEnabled, individualGuideTypes, cornerType, areGuidesConfigured, setAllGuideTypes, setIndividualGuideType, setIndividualGuidesEnabled, resetGuides, setCornerType, resetGuidesAndType, areAllGuidesOfType, guidesAndTypeConfig, getGuideTypeChecklistValue, openPreferencePopup, closePreferencePopup, selectLongerArm, includesOptionsStep, jointOptions, jointOptionTabs, toggleOption, resetOptions, selectedOptions, updateActiveProductFormValue, captureJointImageBase64, addCombination, formatDataForMultipositionUpdate, updateMultiposition, updateCurrentProduct, formatAdditionalOptionsForMultiposition, formatGuides, updateFormDataAttributes, formatActiveSwitchForMultiposition, currentStep, stepLabels, currentStepDescription, stepsAccessible, canGoToPrevStep, canGoToNextStep, goToNextStep, goToPrevStep, goToStep, resetToFirstStep, totalSteps, isOnLastStep, isOnOptionsStep, isOnPreviewStep, openPopup, closePopup, CombinationsProductTable, CombinationsProductsControls, CombinationsSelectorProgressBar, CombinationsStepInfo, ShapeAndAngle, GuidesAndType, PreferenceModal, AdditionalOptions, OptionsPreview, JointsConfiguratorTemplate, JointsParameterList, additionalDescriptions, DownloadButton }
}

})